import React, { useEffect, useMemo, useState } from 'react';
import '../../da-vue.css';
import './CRMRadial.css';
import { ancre } from '../../tutoriel/ancres';
import { comprendreDemande } from './comprendreDemande';
import * as serveurCentre from '../../services/crmCentre';

/**
 * Le tableau de bord du CRM : un cercle, et le client au centre. C'est le
 * centre de l'univers du CRM — on atteint TOUT d'ici : créer un client ou une
 * mission, assigner une ressource, placer un candidat (contrat, formulaire
 * collaborateur, ressource), facturer, valider les CRA, les formulaires, le
 * noyau relationnel.
 *
 * Au départ, aucun client n'est choisi : le cercle montre le PORTEFEUILLE et
 * pose une question ouverte, avec la recherche. Choisir un client le fait
 * entrer dans le cercle — l'anneau s'allume en se traçant — et TOUT l'écran le
 * suit : les gestes autour s'adressent à lui, et leurs panneaux (« Mes
 * tâches », « Assigner une mission ») ne parlent plus que de lui. Pas de
 * listes sous le cercle : ce qu'elles disaient vit dans ces panneaux.
 *
 * Pas de chiffres au centre non plus (30.09) : on n'y vient pas lire un
 * tableau de bord, mais agir. Le cercle tient la question, la recherche juste
 * dessous, puis les ALERTES (01.10) : ce que le CRM calcule et les
 * notifications non lues de la cloche, dites au verbe, ce qui presse en tête ;
 * chacun choisit ce qu'il y voit (« Personnaliser »).
 *
 * Rien n'est inventé ici : tout se recompose depuis les listes que l'écran CRM
 * a déjà chargées (clients, missions, factures, ressources), et chaque geste
 * ouvre le formulaire ou le tiroir qui existait déjà.
 */

const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;

const JOUR = 86400000;
const norm = (s) => String(s || '').trim().toLowerCase();
const debutJour = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const joursEntre = (a, b) => Math.round((debutJour(b) - debutJour(a)) / JOUR);
const chf = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-CH')} CHF`;
const dateCourte = (d) => new Date(d).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' });
const initiales = (nom) => String(nom || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(m => m[0]).join('').toUpperCase() || '?';

const estPayee = (f) => ['payee', 'payée', 'paid'].includes(norm(f.statut));
const estBrouillon = (f) => ['brouillon', 'draft'].includes(norm(f.statut));
const estAvoir = (f) => norm(f.type) === 'avoir';
// Une facture BROUILLON n'est pas partie : elle ne peut pas être en retard. Un
// avoir non plus — c'est nous qui le devons. Le serveur écrit `en_retard`.
const enRetard = (f) => !estPayee(f) && !estBrouillon(f) && !estAvoir(f)
  && (['retard', 'en_retard'].includes(norm(f.statut)) || (f.echeance && debutJour(f.echeance) < debutJour(new Date())));
const ttc = (f) => Number(f.total_ttc ?? (Number(f.total_ht || 0) * (1 + Number(f.tva_taux ?? 8.1) / 100)));
const estActive = (m) => m.statut === 'active';

// Le lien au client : par l'identifiant, sinon par le nom (lignes anciennes).
const duClient = (c) => (x) => (x.client_id != null && String(x.client_id) === String(c.id))
  || (!!x.client && !!c.nom && norm(x.client) === norm(c.nom));

// ── Les alertes du centre, et ce que chacun choisit d'y voir ────────────────
// Deux familles : les ALERTES, calculées depuis les listes du CRM, et les
// NOTIFICATIONS non lues de la cloche qui concernent le CRM. Chacun coche ce
// qu'il veut voir sous la recherche ; « Mes tâches » garde tout.
const ALERTES = [
  { cle: 'retard', groupe: 'Alertes', libelle: 'Factures en retard' },
  { cle: 'brouillon', groupe: 'Alertes', libelle: 'Factures prêtes à envoyer' },
  { cle: 'fin', groupe: 'Alertes', libelle: 'Fins de mission à préparer' },
  { cle: 'ressource', groupe: 'Alertes', libelle: 'Missions sans ressource' },
  { cle: 'cra', groupe: 'Notifications', libelle: 'CRA reçus' },
  { cle: 'signature', groupe: 'Notifications', libelle: 'Documents signés ou refusés' },
  { cle: 'formulaire', groupe: 'Notifications', libelle: 'Formulaires reçus' },
  // Le reste de la cloche (recrutement, mentions…) n'est pas le CRM : à la
  // demande seulement.
  { cle: 'autre', groupe: 'Notifications', libelle: 'Autres notifications', parDefaut: false },
];
const CLE_REGLAGE = 'erpia.crm.centre.alertes';
const lireReglage = () => { try { return JSON.parse(localStorage.getItem(CLE_REGLAGE)) || {}; } catch { return {}; } };
const ecrireReglage = (r) => { try { localStorage.setItem(CLE_REGLAGE, JSON.stringify(r)); } catch { /* navigation privée : le choix vaut pour la séance */ } };
const choisie = (reglage, cle) => reglage[cle] ?? (ALERTES.find(a => a.cle === cle)?.parDefaut !== false);
const lireBrut = (cle) => { try { return localStorage.getItem(cle); } catch { return null; } };

const genreNotif = (n) => {
  const t = norm(n.type), r = norm(n.ref_type);
  if (t.startsWith('cra')) return 'cra';
  if (t === 'signature') return 'signature';
  if (r === 'crm_flux' || /formulaire/.test(norm(n.message))) return 'formulaire';
  return 'autre';
};
const ICONE_NOTIF = { cra: 'calendar2-check', signature: 'pen', formulaire: 'file-earmark-text', autre: 'bell' };
// L'icône d'une alerte servie par le serveur, d'après sa famille.
const ICONE_GENRE = { retard: 'receipt', brouillon: 'send', fin: 'clock-history', ressource: 'person-exclamation', ...ICONE_NOTIF };
// Mettre de côté : pour combien de temps (le serveur accepte 1 à 90 jours).
const DUREES = [{ jours: 1, libelle: 'Demain' }, { jours: 3, libelle: 'Dans 3 jours' }, { jours: 7, libelle: 'Dans 7 jours' }, { jours: 30, libelle: 'Dans 30 jours' }];
// Aucun emoji à l'écran : le serveur en met parfois en tête de message.
const sansPictos = (txt) => String(txt || '').replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u, '').trim();
const depuis = (d) => {
  const t = new Date(d); if (Number.isNaN(t.getTime())) return '';
  const min = Math.round((Date.now() - t.getTime()) / 60000);
  if (min < 60) return min <= 1 ? "À l'instant" : `Il y a ${min} min`;
  if (min < 24 * 60) return `Il y a ${Math.round(min / 60)} h`;
  return joursEntre(t, new Date()) === 1 ? 'Hier' : dateCourte(t);
};

const nomRessource = (r) => [r?.prenom, r?.nom].filter(Boolean).join(' ') || r?.email || 'Ressource';

// Les gestes épinglés, posés sur une ellipse autour du cercle (scène
// 1100 × 560, centre 550 × 280) — tous les 40° quand les neuf y sont.
const CENTRE = { x: 550, y: 280 };
const RAYON = 205;
// Le ton de la pastille d'icône (maquette du 30.09) : corail pour ce qui
// ATTEND ou se lance — les tâches, une mission à créer —, vert sauge pour
// le reste des gestes.
const GESTES = [
  { cle: 'client', icone: 'person-fill-add', ton: 'vert', libelle: 'Nouveau client', mots: 'nouveau client societe entreprise ajouter compte' },
  { cle: 'mission', icone: 'briefcase', ton: 'corail', libelle: 'Créer une mission', mots: 'creer mission opportunite nouvelle' },
  { cle: 'assigner', icone: 'people-fill', ton: 'vert', libelle: 'Assigner une mission', mots: 'assigner mission ressource pourvoir disponible' },
  { cle: 'placer', icone: 'person', ton: 'vert', libelle: 'Placer un candidat', mots: 'placer candidat contrat formulaire placement embaucher' },
  { cle: 'facture', icone: 'send', ton: 'vert', libelle: 'Envoyer une facture', mots: 'envoyer facture facturer facturation' },
  { cle: 'cra', icone: 'clock', ton: 'vert', libelle: 'Valider les CRA', mots: 'valider cra releves temps' },
  { cle: 'formulaires', icone: 'file-earmark-text', ton: 'vert', libelle: 'Formulaires', mots: 'formulaires formulaire composer envoyer' },
  { cle: 'noyau', icone: 'eye', ton: 'vert', libelle: 'Voir le noyau', mots: 'noyau relations relationnel' },
  { cle: 'taches', icone: 'check2-square', ton: 'corail', libelle: 'Mes tâches', mots: 'mes taches a traiter alertes' },
];
// Les gestes ÉPINGLÉS autour du cercle : chacun garde ceux qu'il veut — neuf,
// trois, aucun. Un geste retiré reste dans la recherche du cercle.
const CLE_GESTES = 'erpia.crm.centre.gestes';
const lireGestes = () => {
  try {
    const v = JSON.parse(localStorage.getItem(CLE_GESTES));
    if (Array.isArray(v)) return GESTES.map(g => g.cle).filter(c => v.includes(c));
  } catch { /* rien d'enregistré : tous */ }
  return GESTES.map(g => g.cle);
};
const ecrireGestes = (v) => { try { localStorage.setItem(CLE_GESTES, JSON.stringify(v)); } catch { /* le choix vaut pour la séance */ } };
const sansAccents = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// L'orbite est une ellipse : les gestes sont POSÉS dessus, à intervalles
// égaux, en partant du haut (deux : de part et d'autre ; quatre : en
// diagonale). Une capsule qui mordrait sur l'anneau s'écarte sur le côté.
const ORBITE = { rx: 400, ry: 250 };
// La capsule vue de l'anneau (coins arrondis déduits) ; les neuf positions
// d'origine la respectent toutes, elles ne bougent donc pas.
const DEMI_CAPSULE = { l: 110, h: 30 };
const ecart = (x, y) => Math.hypot(Math.max(Math.abs(x - CENTRE.x) - DEMI_CAPSULE.l, 0), Math.max(Math.abs(y - CENTRE.y) - DEMI_CAPSULE.h, 0));
const placer = (gestes) => {
  const n = gestes.length;
  const depart = n === 2 ? 90 : n === 4 ? 45 : 0;
  return gestes.map((g, i) => {
    const a = ((depart + (i * 360) / n) * Math.PI) / 180;
    let x = CENTRE.x + ORBITE.rx * Math.sin(a);
    const y = CENTRE.y - ORBITE.ry * Math.cos(a);
    const sens = Math.round(x) === CENTRE.x ? 0 : Math.sign(x - CENTRE.x);
    while (sens && ecart(x, y) < RAYON + 1 && Math.abs(x - CENTRE.x) < 430) x += sens * 4;
    return { ...g, x: Math.round(x), y: Math.round(y) };
  });
};
const surAnneau = (s) => {
  const a = Math.atan2(s.y - CENTRE.y, s.x - CENTRE.x);
  return { x: CENTRE.x + RAYON * Math.cos(a), y: CENTRE.y + RAYON * Math.sin(a), a };
};

// Une alerte : ce qu'il faut faire, pour qui, et depuis quand. Un clic y va.
// « Plus tard » la met de côté (seulement quand le serveur sait le faire).
function LigneAlerte({ a, onPlusTard }) {
  return (
    <li className="crr-alerte-ligne">
      <button type="button" className="crr-alerte" data-alerte={a.alerte ? 'oui' : undefined} onClick={a.faire}
        title={[a.texte, a.detail, a.quand].filter(Boolean).join(' · ')}>
        <BI icon={a.icone} />
        <span className="crr-alerte-texte">{a.texte}{a.detail && <small> · {a.detail}</small>}</span>
        {a.quand && <span className="crr-alerte-quand">{a.quand}</span>}
      </button>
      {onPlusTard && (
        <button type="button" className="crr-alerte-plus-tard" onClick={() => onPlusTard(a)}
          aria-label={`Mettre de côté : ${a.texte}`} title="Mettre de côté">
          <BI icon="alarm" />
        </button>
      )}
    </li>
  );
}

export default function CRMRadial({
  clients, missions, factures, resources, selectionId, onSelection,
  recherche, resultats, onRecherche, onResultat,
  onNouveauClient, onCreerMission, onAssigner, onPlacer, onFacturer, onCra, onFormulaires, onNoyau, onFicheClient,
  onOuvrirFacture, onOuvrirMission, onOuvrirRessource, onVoir,
  notifications = [], onOuvrirNotif, onLireNotif,
}) {
  const client = selectionId != null ? clients.find(c => String(c.id) === String(selectionId)) || null : null;
  const [rechercheOuverte, setRechercheOuverte] = useState(false);
  // Un panneau ouvert à la fois, attaché à son geste : « Mes tâches » ou
  // « Assigner une mission ». Il se ferme au clic dehors, sur Échap, et quand
  // le client au centre change.
  const [panneau, setPanneau] = useState(null);
  useEffect(() => { setPanneau(null); }, [selectionId]);
  useEffect(() => {
    if (!panneau) return undefined;
    const dehors = (e) => { if (!e.target.closest?.('.crr-sat-ancre[data-ouvert]')) setPanneau(null); };
    const echap = (e) => { if (e.key === 'Escape') setPanneau(null); };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
  }, [panneau]);

  // ── Le serveur : alertes, réglage, mises de côté ──────────────────────────
  // Le réglage SUIT LA PERSONNE d'un poste à l'autre : il est lu au serveur ;
  // celui que ce navigateur gardait (d'avant) y est recopié une fois. Tant que
  // la route manque, ce navigateur reste la mémoire — rien ne s'écrit ailleurs.
  const [reglageServeur, setReglageServeur] = useState(false);
  const [version, setVersion] = useState(0);
  const relire = () => setVersion(v => v + 1);
  const [epingles, setEpingles] = useState(lireGestes);
  const choisirGestes = (v) => {
    const ordre = GESTES.map(g => g.cle).filter(c => v.includes(c));
    ecrireGestes(ordre); setEpingles(ordre);
    if (reglageServeur) serveurCentre.ecrireReglage({ actions: ordre });
  };
  const satellites = useMemo(() => placer(GESTES.filter(g => epingles.includes(g.cle))), [epingles]);
  const [reglageGestes, setReglageGestes] = useState(false);
  useEffect(() => {
    if (!reglageGestes) return undefined;
    const dehors = (e) => { if (!e.target.closest?.('.crr-tete-reglage')) setReglageGestes(false); };
    const echap = (e) => { if (e.key === 'Escape') setReglageGestes(false); };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
  }, [reglageGestes]);

  // Le réglage des alertes (par personne, sur ce poste) et la fenêtre qui
  // s'ouvre sous elles : toutes les alertes, ou le réglage.
  const [reglage, setReglage] = useState(lireReglage);
  const basculer = (cle) => {
    const n = { ...reglage, [cle]: !choisie(reglage, cle) };
    setReglage(n); ecrireReglage(n);
    // Une famille ajoutée doit être relue : le serveur ne l'avait pas servie.
    if (reglageServeur) serveurCentre.ecrireReglage({ alertes: { [cle]: n[cle] } }).then(relire);
  };
  useEffect(() => {
    let annule = false;
    serveurCentre.lireReglage().then(async (r) => {
      if (annule || !r.ok) return;
      setReglageServeur(true);
      const avaitActions = !!lireBrut(CLE_GESTES), avaitAlertes = !!lireBrut(CLE_REGLAGE);
      if (!r.data.enregistre_le && (avaitActions || avaitAlertes)) {
        await serveurCentre.ecrireReglage({ ...(avaitActions ? { actions: lireGestes() } : {}), ...(avaitAlertes ? { alertes: lireReglage() } : {}) });
        if (!annule) relire();
        return;
      }
      const actions = Array.isArray(r.data.actions) ? r.data.actions : GESTES.map(g => g.cle);
      setEpingles(actions); ecrireGestes(actions);
      if (r.data.alertes) { setReglage(r.data.alertes); ecrireReglage(r.data.alertes); }
    });
    return () => { annule = true; };
  }, []);
  const [fenetre, setFenetre] = useState(null);
  useEffect(() => { setFenetre(null); }, [selectionId]);
  useEffect(() => {
    if (!fenetre) return undefined;
    const dehors = (e) => { if (!e.target.closest?.('.crr-alertes')) setFenetre(null); };
    const echap = (e) => { if (e.key === 'Escape') setFenetre(null); };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
  }, [fenetre]);

  // Les alertes : la MÊME liste que lit l'assistant, relue quand le client au
  // centre change, quand une liste du CRM bouge, et après une mise de côté.
  const empreinte = useMemo(() => [
    missions.map(m => `${m.id}:${m.statut}:${m.resource_id ?? ''}:${m.date_fin ?? ''}`).join('|'),
    factures.map(f => `${f.id}:${f.statut}:${f.echeance ?? ''}`).join('|'),
    notifications.filter(n => !(n.lu || n.read)).map(n => n.id).join(','),
  ].join('#'), [missions, factures, notifications]);
  const [servies, setServies] = useState(null);
  useEffect(() => {
    let annule = false;
    serveurCentre.lireAlertes(selectionId ?? null).then(r => { if (!annule) setServies(r.ok ? r.data : null); });
    return () => { annule = true; };
  }, [selectionId, version, empreinte]);

  // Mettre de côté : l'alerte choisie, la note, et l'erreur du serveur s'il
  // refuse (dite telle quelle).
  const [aMettre, setAMettre] = useState(null);
  const [noteDeCote, setNoteDeCote] = useState('');
  const [erreurDeCote, setErreurDeCote] = useState(null);
  const plusTard = servies ? (a) => { setAMettre(a); setNoteDeCote(''); setErreurDeCote(null); setFenetre('decote'); } : null;
  const mettreDeCote = async (jours) => {
    const r = await serveurCentre.mettreDeCote(aMettre.cle, { jours, note: noteDeCote.trim() });
    if (!r.ok) { setErreurDeCote(r.error || "L'alerte n'a pas pu être mise de côté."); return; }
    setFenetre(null); setAMettre(null); relire();
  };
  const remettre = async (cle) => { const r = await serveurCentre.remettre(cle); if (r.ok) relire(); };

  // ── Ce que le cercle regarde : un client, ou tout le portefeuille ──────────
  const vue = useMemo(() => {
    const aujourdhui = debutJour(new Date());
    const mis = client ? missions.filter(duClient(client)) : missions;
    const fac = client ? factures.filter(duClient(client)) : factures;
    const nomClientDe = (x) => x.client || clients.find(c => String(c.id) === String(x.client_id))?.nom || 'Client';

    const actives = mis.filter(estActive);

    const retards = fac.filter(enRetard)
      .map(f => ({ f, jours: f.echeance ? Math.max(1, joursEntre(f.echeance, aujourdhui)) : null }))
      .sort((a, b) => (b.jours || 0) - (a.jours || 0));
    // Une facture en BROUILLON attend son envoi : c'est un geste, pas un état.
    const brouillons = fac.filter(f => estBrouillon(f) && !estAvoir(f))
      .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    const fins = actives
      .filter(m => m.date_fin && joursEntre(aujourdhui, m.date_fin) >= 0 && joursEntre(aujourdhui, m.date_fin) <= 60)
      .sort((a, b) => new Date(a.date_fin) - new Date(b.date_fin));
    const sansRessource = mis.filter(m => m.resource_id == null && ['active', 'draft', 'brouillon'].includes(norm(m.statut)));

    const aTraiter = [
      ...retards.map(({ f, jours }) => ({
        cle: 'f' + f.id, genre: 'retard', icone: 'receipt', titre: `${f.numero || 'Facture'} en retard`, sous: `${nomClientDe(f)} · ${chf(ttc(f))}`,
        geste: `Relancer ${f.numero || 'la facture'}`, detail: client ? chf(ttc(f)) : nomClientDe(f),
        quand: jours ? `+${jours} j` : 'En retard', alerte: true, faire: () => onOuvrirFacture(f),
      })),
      ...brouillons.map(f => ({
        cle: 'b' + f.id, genre: 'brouillon', icone: 'send', titre: `${f.numero || 'Facture'} prête à envoyer`, sous: `${nomClientDe(f)} · ${chf(ttc(f))}`,
        geste: `Envoyer ${f.numero || 'la facture'}`, detail: client ? chf(ttc(f)) : nomClientDe(f),
        quand: 'Brouillon', alerte: false, faire: () => onOuvrirFacture(f),
      })),
      ...fins.map(m => {
        const j = joursEntre(aujourdhui, m.date_fin);
        return {
          cle: 'm' + m.id, genre: 'fin', icone: 'clock-history', titre: 'Fin de mission à préparer', sous: `${m.titre || 'Mission'} · ${nomClientDe(m)}`,
          geste: 'Préparer la fin de mission', detail: m.titre || 'Mission',
          quand: j === 0 ? "Aujourd'hui" : j === 1 ? 'Demain' : dateCourte(m.date_fin), alerte: j <= 14, faire: () => onOuvrirMission(m),
        };
      }),
      ...sansRessource.map(m => ({
        cle: 'r' + m.id, genre: 'ressource', icone: 'person-exclamation', titre: 'Ressource à confirmer', sous: `${m.titre || 'Mission'} · ${nomClientDe(m)}`,
        geste: 'Confirmer la ressource', detail: m.titre || 'Mission',
        quand: 'À confirmer', alerte: false, faire: () => onOuvrirMission(m),
      })),
    ];

    // Disponibles : aucune mission active, une occupation partielle, ou une fin
    // dans les 60 jours. Une ressource sortie (terminée, supprimée) ne l'est pas.
    const disponibles = resources
      .filter(r => !['termine', 'terminee', 'terminée', 'supprime'].includes(norm(r.statut)))
      .map(r => {
        const siennes = missions.filter(m => String(m.resource_id) === String(r.id) && estActive(m)
          && (!m.date_fin || debutJour(m.date_fin) >= aujourdhui));
        if (!siennes.length) return { r, des: null, part: 100 };
        const occupe = siennes.reduce((s, m) => s + (Number(m.taux_activite) || 100), 0);
        if (occupe < 100) return { r, des: null, part: 100 - occupe };
        if (siennes.some(m => !m.date_fin)) return null;
        const fin = new Date(Math.max(...siennes.map(m => debutJour(m.date_fin).getTime())));
        if (joursEntre(aujourdhui, fin) > 60) return null;
        return { r, des: new Date(fin.getTime() + JOUR), part: 100 };
      })
      .filter(Boolean)
      .sort((a, b) => (a.des ? a.des.getTime() : 0) - (b.des ? b.des.getTime() : 0) || b.part - a.part);

    return {
      actif: actives.length > 0, retards, aTraiter, disponibles, sansRessource, nomClientDe,
    };
  }, [client, clients, missions, factures, resources, onOuvrirFacture, onOuvrirMission]);

  // Ouvrir une notification : un CRA reçu se valide dans l'onglet CRA, un
  // formulaire reçu sur la ressource ; le reste suit le chemin de la cloche.
  // onLireNotif prend l'identifiant ; onOuvrirNotif, la notification.
  const ouvrirNotif = (n, genre) => {
    const r = genre === 'formulaire' && norm(n.ref_type) === 'resource'
      ? resources.find(x => String(x.id) === String(n.ref_id)) : null;
    if (genre === 'cra' || r) { onLireNotif?.(n.id); if (r) onOuvrirRessource(r); else onCra(); return; }
    if (onOuvrirNotif) onOuvrirNotif(n); else onLireNotif?.(n.id);
  };
  // Une alerte SERVIE : la cible dit où aller.
  const depuisServeur = (a) => ({
    cle: a.cle, genre: a.genre, icone: ICONE_GENRE[a.genre] || 'bell', texte: a.texte, detail: a.detail, quand: a.quand,
    alerte: !!a.urgent, deCote: a.de_cote || null,
    faire: () => {
      const c = a.cible || {};
      if (c.type === 'facture') onOuvrirFacture({ id: c.id, numero: c.numero });
      else if (c.type === 'mission') onOuvrirMission(missions.find(m => String(m.id) === String(c.id)) || { id: c.id });
      else if (c.type === 'notification') ouvrirNotif({ id: c.id, type: c.notification_type, ref_type: c.ref_type, ref_id: c.ref_id, message: a.texte }, a.genre);
    },
  });

  // Sous la recherche : les alertes choisies, puis les notifications non lues
  // choisies. Le serveur les sert (la même liste que l'assistant) ; sans lui,
  // l'écran les calcule depuis les listes qu'il a déjà. Les notifications ne
  // portent pas de client : elles ne s'affichent qu'au portefeuille. Ce qui
  // presse (retard, fin sous 14 jours) passe devant ; les notifications, plus
  // récentes d'abord, ensuite.
  const signalements = useMemo(() => {
    if (servies) return servies.alertes.filter(a => choisie(reglage, a.genre)).map(depuisServeur);
    const alertes = vue.aTraiter.filter(t => choisie(reglage, t.genre))
      .map(t => ({ cle: t.cle, icone: t.icone, texte: t.geste, detail: t.detail, quand: t.quand, alerte: t.alerte, faire: t.faire }));
    const notifs = client ? [] : notifications
      .filter(n => !(n.lu || n.read))
      .map(n => ({ n, genre: genreNotif(n) }))
      .filter(({ genre }) => choisie(reglage, genre))
      .sort((a, b) => new Date(b.n.created_at || 0) - new Date(a.n.created_at || 0))
      .map(({ n, genre }) => ({
        cle: 'n' + n.id, icone: ICONE_NOTIF[genre], texte: sansPictos(n.message), quand: depuis(n.created_at), alerte: false,
        faire: () => ouvrirNotif(n, genre),
      }));
    return [...alertes.filter(a => a.alerte), ...notifs, ...alertes.filter(a => !a.alerte)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servies, vue.aTraiter, notifications, reglage, client, resources, missions, onCra, onLireNotif, onOuvrirNotif, onOuvrirRessource, onOuvrirFacture, onOuvrirMission]);
  // « Mes tâches » : tout ce que le CRM calcule, réglage ou pas ; les mises de
  // côté y restent, marquées.
  const taches = useMemo(() => (servies
    ? servies.taches.map(depuisServeur)
    : vue.aTraiter.map(t => ({ cle: t.cle, icone: t.icone, texte: t.geste, detail: t.detail, quand: t.quand, alerte: t.alerte, faire: t.faire })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , [servies, vue.aTraiter, missions]);
  const tachesActives = taches.filter(t => !t.deCote);
  const deCoteListe = servies ? servies.de_cote.map(depuisServeur) : [];
  const aucuneChoisie = ALERTES.every(a => !choisie(reglage, a.cle));
  const VISIBLES = 3;

  // La recherche du cercle est celle du CRM : un client trouvé ENTRE dans le
  // cercle ; une mission, une facture ou une ressource s'ouvre comme partout.
  const choisir = (res) => {
    setRechercheOuverte(false);
    if (res.type === 'demande') { onRecherche(''); executer(res.demande.action); return; }
    if (res.type === 'geste') { onRecherche(''); lancerGeste(res.id); return; }
    if (res.type === 'client') { onRecherche(''); onSelection(res.id); return; }
    onResultat(res);
  };
  useEffect(() => { if (!recherche) setRechercheOuverte(false); }, [recherche]);

  // Les gestes se cherchent comme le reste : un geste retiré du tableau
  // s'atteint encore en tapant son nom (« facture », « cra », « placer »).
  const q = sansAccents(recherche).trim();
  const gestesTrouves = q ? GESTES.filter(g => sansAccents(`${g.libelle} ${g.mots}`).includes(q)).slice(0, 3)
    .map(g => ({ type: 'geste', id: g.cle, label: g.libelle, meta: epingles.includes(g.cle) ? 'Autour du cercle' : 'Retiré du tableau', icon: g.icone })) : [];
  // Une DEMANDE comprise passe devant : « facturer vaudoise » ouvre la facture
  // préremplie, « assigner emma java » la mission avec sa ressource.
  const demandes = q.length >= 3 ? comprendreDemande(recherche, { clients, ressources: resources, missions, factures })
    .map(d => ({ type: 'demande', id: d.cle, label: d.libelle, meta: d.detail, icon: d.icone, demande: d })) : [];
  const tousResultats = [...demandes, ...gestesTrouves, ...resultats];

  const nom = client?.nom || 'Client';
  const aPourvoir = vue.sansRessource.length;
  const sat = {
    client: { titre: 'Nouveau client', sous: 'Ajouter une société', faire: () => onNouveauClient('') },
    mission: { titre: 'Créer une mission', sous: client ? `Pour ${nom}` : 'Nouvelle opportunité', faire: () => onCreerMission(client) },
    assigner: {
      titre: 'Assigner une mission', sous: aPourvoir ? `${aPourvoir} mission${aPourvoir > 1 ? 's' : ''} à pourvoir` : 'Rien à pourvoir',
      faire: () => setPanneau(o => (o === 'assigner' ? null : 'assigner')),
    },
    placer: { titre: 'Placer un candidat', sous: 'Contrat et formulaire', faire: onPlacer },
    facture: { titre: 'Envoyer une facture', sous: client ? `Facturer ${nom}` : 'Générer et envoyer', faire: () => onFacturer(client) },
    cra: { titre: 'Valider les CRA', sous: 'Relevés de temps du mois', faire: onCra },
    formulaires: onFormulaires
      ? { titre: 'Formulaires', sous: 'Composer et envoyer', faire: onFormulaires }
      : { titre: 'Formulaires', sous: 'Module non activé', faire: null },
    noyau: { titre: 'Voir le noyau', sous: client ? `Les relations de ${nom}` : 'Vue globale des relations', faire: () => onNoyau(client) },
    taches: {
      titre: 'Mes tâches', sous: tachesActives.length ? `${tachesActives.length} à traiter${client ? ` pour ${nom}` : ''}` : 'Rien en attente',
      compte: tachesActives.length,
      faire: () => setPanneau(o => (o === 'taches' ? null : 'taches')),
    },
  };

  // Ce qu'une demande ouvre : les formulaires et écrans qui existent déjà —
  // rien ne s'écrit sans un clic de plus.
  function executer(a) {
    if (a.type === 'facturer') onFacturer(a.client);
    else if (a.type === 'mission') onCreerMission(a.client);
    else if (a.type === 'nouveau_client') onNouveauClient(a.nom);
    else if (a.type === 'assigner') onAssigner(a.mission, a.ressource);
    else if (a.type === 'placer') onPlacer();
    else if (a.type === 'cra') onCra(a.ressource);
    else if (a.type === 'noyau') onNoyau(a.client);
    else if (a.type === 'fiche') onFicheClient(a.client);
    else if (a.type === 'facture') onOuvrirFacture(a.facture);
  }

  // Un geste lancé depuis la recherche : un panneau sans sa capsule ouvre
  // l'équivalent (les alertes, la liste des missions).
  function lancerGeste(cle) {
    const pose = epingles.includes(cle);
    if (cle === 'taches') { if (pose) setPanneau('taches'); else setFenetre('tout'); return; }
    if (cle === 'assigner') { if (pose) setPanneau('assigner'); else onVoir('missions', client); return; }
    sat[cle]?.faire?.();
  }

  return (
    <section className="crr" data-da="vue" aria-label="Centre du CRM">
      <div className="crr-tete">
        <div className="crr-tete-titre">
          <strong>Centre radial</strong>
          <span>{client ? `Vue 360° de ${nom} : ses missions, ses ressources et ses factures.` : 'Vue 360° de vos clients, de leurs missions et de leurs factures.'}</span>
        </div>
        <div className="crr-tete-reglage">
          <button type="button" className="crr-tete-bouton" aria-expanded={reglageGestes} onClick={() => setReglageGestes(o => !o)}>
            <BI icon="pin-angle" />Actions affichées<em>{epingles.length}</em>
          </button>
          {reglageGestes && (
            <div className="crr-fenetre crr-fenetre--droite" role="dialog" aria-label="Choisir les actions affichées">
              <div className="crr-panneau-titre">Les actions autour du cercle</div>
              {GESTES.map(g => (
                <label key={g.cle} className="crr-reglage-ligne">
                  <input type="checkbox" checked={epingles.includes(g.cle)}
                    onChange={() => choisirGestes(epingles.includes(g.cle) ? epingles.filter(c => c !== g.cle) : [...epingles, g.cle])} />
                  <span className="crr-sat-icone crr-sat-icone--mini" data-ton={g.ton}><BI icon={g.icone} /></span>
                  <span>{g.libelle}</span>
                </label>
              ))}
              <div className="crr-reglage-actions">
                <button type="button" onClick={() => choisirGestes(GESTES.map(g => g.cle))}>Tous</button>
                <button type="button" onClick={() => choisirGestes([])}>Aucun</button>
              </div>
              <p className="crr-reglage-note">Une action retirée reste à portée : tapez son nom dans la recherche du cercle.</p>
            </div>
          )}
        </div>
      </div>

      <div className="crr-scene" data-client={client ? 'oui' : 'non'} data-gestes={satellites.length}>
        <svg className="crr-orbites" viewBox="0 0 1100 560" aria-hidden="true" focusable="false">
          {satellites.length > 0 && <ellipse className="crr-orbite" cx={CENTRE.x} cy={CENTRE.y} rx={ORBITE.rx} ry={ORBITE.ry} />}
          <circle className="crr-anneau" cx={CENTRE.x} cy={CENTRE.y} r={RAYON} />
          {client && (
            <circle key={'trace-' + client.id} className="crr-anneau-vif" cx={CENTRE.x} cy={CENTRE.y} r={RAYON}
              pathLength="1" transform={`rotate(-90 ${CENTRE.x} ${CENTRE.y})`} />
          )}
          {satellites.map((s, i) => {
            const p = surAnneau(s);
            return (
              <g key={s.cle + (client ? '-' + client.id : '')} className="crr-attache" style={{ '--rang': i }}>
                {/* La liaison est CONTINUE : de l'anneau jusqu'au geste. Elle file
                    jusqu'à son centre, et la capsule (opaque) en couvre le bout. */}
                <line x1={p.x} y1={p.y} x2={s.x} y2={s.y} />
                <circle cx={p.x} cy={p.y} r="5" />
              </g>
            );
          })}
        </svg>

        <div className="crr-coeur" aria-live="polite" {...ancre('crm-centre')}>
          <div key={client ? 'c-' + client.id : 'portefeuille'} className="crr-coeur-contenu">
            <div className="crr-kicker">
              {client ? 'Client sélectionné' : 'Portefeuille'}
              {client && (
                <button type="button" className="crr-retirer" onClick={() => onSelection(null)} aria-label="Revenir au portefeuille" title="Revenir au portefeuille">
                  <BI icon="x-lg" />
                </button>
              )}
            </div>
            {/* Sans client, le centre pose une question OUVERTE : on vient peut-être
                ajouter une ressource ou vérifier des CRA, pas choisir un client.
                Dessous : la recherche, puis les alertes — rien d'autre. */}
            <div className="crr-nom" title={client ? nom : undefined}>{client ? nom : 'Que voulez-vous faire ?'}</div>
            {client && (
              <div className="crr-statut">
                Compte client · <span data-actif={vue.actif ? 'oui' : 'non'}>{vue.actif ? 'Actif' : 'Sans mission active'}</span>
              </div>
            )}

            <div className="crr-recherche">
              <label className="crr-champ">
                <BI icon="search" />
                <input value={recherche} placeholder={client ? 'Mission, ressource, facture…' : 'Client, ressource, mission…'}
                  aria-label="Rechercher dans le CRM"
                  onChange={e => { onRecherche(e.target.value); setRechercheOuverte(true); }}
                  onFocus={() => recherche && setRechercheOuverte(true)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') { onRecherche(''); setRechercheOuverte(false); }
                    if (e.key === 'Enter' && tousResultats[0]) choisir(tousResultats[0]);
                  }} />
              </label>
              {rechercheOuverte && recherche.trim() && (
                <div className="crr-resultats" role="listbox" aria-label="Résultats">
                  {tousResultats.length ? tousResultats.map(r => (
                    <button key={r.type + '-' + r.id} type="button" role="option" aria-selected="false" className="crr-resultat" onClick={() => choisir(r)}>
                      <BI icon={r.icon} />
                      <span><strong>{r.label}</strong><small>{r.meta || '—'}</small></span>
                      <em>{r.type === 'demande' ? 'Demande' : r.type === 'geste' ? 'Action' : r.type === 'client' ? 'Au centre' : r.type === 'action' ? 'Action' : r.type === 'resource' ? 'Ressource' : r.type === 'mission' ? 'Mission' : 'Facture'}</em>
                    </button>
                  )) : <div className="crr-resultats-vide">Rien ne correspond dans les clients, missions, ressources ou factures.</div>}
                </div>
              )}
            </div>

            <div className="crr-alertes">
              <div className="crr-alertes-tete">
                <span>Alertes{signalements.length ? <em>{signalements.length}</em> : null}</span>
                <button type="button" className="crr-alertes-regler" aria-expanded={fenetre === 'reglage'}
                  onClick={() => setFenetre(f => (f === 'reglage' ? null : 'reglage'))}>
                  <BI icon="sliders" />Personnaliser
                </button>
              </div>
              {signalements.length ? (
                <ul className="crr-alertes-liste">
                  {signalements.slice(0, VISIBLES).map(a => <LigneAlerte key={a.cle} a={a} onPlusTard={plusTard} />)}
                </ul>
              ) : (
                <p className="crr-alertes-vide">
                  {/* Le nom du client est juste au-dessus : la phrase ne le répète pas. */}
                  {aucuneChoisie ? 'Aucune alerte choisie.' : 'Rien à signaler : tout est à jour.'}
                </p>
              )}
              {(signalements.length > VISIBLES || deCoteListe.length > 0) && (
                <button type="button" className="crr-alertes-plus" aria-expanded={fenetre === 'tout'}
                  onClick={() => setFenetre(f => (f === 'tout' ? null : 'tout'))}>
                  {signalements.length > VISIBLES ? `Voir les ${signalements.length} alertes` : `${deCoteListe.length} mise${deCoteListe.length > 1 ? 's' : ''} de côté`}
                </button>
              )}

              {fenetre === 'tout' && (
                <div className="crr-fenetre crr-fenetre--liste" role="dialog" aria-label="Toutes les alertes">
                  <div className="crr-panneau-titre">Alertes{client ? ` · ${nom}` : ''}</div>
                  {signalements.length ? (
                    <ul className="crr-alertes-liste">
                      {signalements.slice(0, 12).map(a => <LigneAlerte key={a.cle} a={a} onPlusTard={plusTard} />)}
                    </ul>
                  ) : <p className="crr-panneau-vide">Rien à signaler : tout est à jour.</p>}
                  {signalements.length > 12 && <p className="crr-panneau-vide">… et {signalements.length - 12} autre{signalements.length - 12 > 1 ? 's' : ''} dans « Mes tâches » et la cloche.</p>}
                  {deCoteListe.length > 0 && (
                    <>
                      <div className="crr-panneau-titre crr-panneau-titre--suite">Mises de côté</div>
                      <ul className="crr-alertes-liste">
                        {deCoteListe.map(a => (
                          <li key={a.cle} className="crr-alerte-ligne crr-alerte-ligne--de-cote">
                            <span className="crr-alerte" title={[a.texte, a.detail, a.deCote?.note].filter(Boolean).join(' · ')}>
                              <BI icon="alarm" />
                              <span className="crr-alerte-texte">{a.texte}{a.deCote?.note && <small> · {a.deCote.note}</small>}</span>
                              <span className="crr-alerte-quand">Revient le {a.deCote?.revient}</span>
                            </span>
                            <button type="button" className="crr-alerte-remettre" onClick={() => remettre(a.cle)}>Remettre</button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
              {fenetre === 'decote' && aMettre && (
                <div className="crr-fenetre" role="dialog" aria-label="Mettre l'alerte de côté">
                  <div className="crr-panneau-titre">Mettre de côté</div>
                  <p className="crr-decote-quoi">{aMettre.texte}{aMettre.detail ? ` · ${aMettre.detail}` : ''}</p>
                  <input className="crr-decote-note" value={noteDeCote} onChange={e => setNoteDeCote(e.target.value)} maxLength={300}
                    placeholder="Pourquoi ? ex. relancé par téléphone" aria-label="Note" autoFocus />
                  <div className="crr-reglage-actions crr-decote-durees">
                    {DUREES.map(d => <button key={d.jours} type="button" onClick={() => mettreDeCote(d.jours)}>{d.libelle}</button>)}
                  </div>
                  {erreurDeCote && <p className="crr-decote-erreur" role="alert">{erreurDeCote}</p>}
                  <p className="crr-reglage-note">Elle quitte votre cercle et revient seule ce jour-là ; vos collègues la voient toujours.</p>
                </div>
              )}
              {fenetre === 'reglage' && (
                <div className="crr-fenetre crr-fenetre--reglage" role="dialog" aria-label="Choisir les alertes">
                  <div className="crr-panneau-titre">Ce que le centre vous signale</div>
                  <div className="crr-reglage-colonnes">
                  {['Alertes', 'Notifications'].map(g => (
                    <fieldset key={g} className="crr-reglage-groupe">
                      <legend>{g}</legend>
                      {ALERTES.filter(a => a.groupe === g).map(a => (
                        <label key={a.cle} className="crr-reglage-ligne">
                          <input type="checkbox" checked={choisie(reglage, a.cle)} onChange={() => basculer(a.cle)} />
                          <span>{a.libelle}</span>
                        </label>
                      ))}
                    </fieldset>
                  ))}
                  </div>
                  <p className="crr-reglage-note">Les notifications s'affichent quand aucun client n'est au centre. « Mes tâches » garde toutes les alertes.</p>
                </div>
              )}
            </div>

            {client && (
              <button type="button" className="crr-lien" onClick={() => onFicheClient(client)}>Voir la fiche client <BI icon="arrow-right" /></button>
            )}
          </div>
        </div>

        {satellites.map((s, i) => {
          const d = sat[s.cle];
          const aPanneau = s.cle === 'assigner' || s.cle === 'taches';
          const place = { left: `${(s.x / 1100) * 100}%`, top: `${(s.y / 560) * 100}%`, '--rang': i };
          const bouton = (
            <button type="button" className="crr-sat" data-cle={s.cle} onClick={d.faire || undefined} disabled={!d.faire}
              aria-expanded={aPanneau ? panneau === s.cle : undefined} style={aPanneau ? undefined : place}>
              <span className="crr-sat-icone" data-ton={s.ton}><BI icon={s.icone} /></span>
              {/* La petite ligne suit le client : elle se rejoue quand il change. */}
              <span className="crr-sat-texte"><strong>{d.titre}</strong><small key={client ? 'c' + client.id : 'p'}>{d.sous}</small></span>
              {d.compte > 0 && <span className="crr-sat-compte" aria-label={`${d.compte} à traiter`}>{d.compte}</span>}
            </button>
          );
          if (!aPanneau) return <React.Fragment key={s.cle}>{bouton}</React.Fragment>;
          const ouvert = panneau === s.cle;
          return (
            <div key={s.cle} className="crr-sat-ancre" data-ouvert={ouvert ? 'oui' : undefined} style={place}>
              {bouton}
              {ouvert && s.cle === 'taches' && (
                // Ce qui attend : une facture en retard, une fin de mission à
                // préparer, une ressource à confirmer — pour le client au centre.
                <div className="crr-panneau" data-cote={s.x < CENTRE.x ? 'gauche' : 'droite'} role="dialog" aria-label="À traiter">
                  <div className="crr-panneau-titre">À traiter{client ? ` · ${nom}` : ''}</div>
                  {taches.length ? taches.slice(0, 7).map(t => (
                    <button key={t.cle} type="button" className="crr-panneau-ligne crr-panneau-ligne--rang" onClick={() => { setPanneau(null); t.faire(); }}>
                      <span className="crr-panneau-icone"><BI icon={t.icone} /></span>
                      <span className="crr-panneau-texte"><strong>{t.texte}</strong><small>{[t.detail, t.deCote ? `de côté jusqu'au ${t.deCote.revient}` : null].filter(Boolean).join(' · ')}</small></span>
                      <span className="crr-panneau-quand" data-alerte={t.alerte && !t.deCote ? 'oui' : 'non'}>{t.quand}</span>
                    </button>
                  )) : (
                    <p className="crr-panneau-vide">Rien à traiter{client ? ` pour ${nom}` : ' : le portefeuille est à jour'}.</p>
                  )}
                  {taches.length > 7 && <p className="crr-panneau-vide">… et {taches.length - 7} autre{taches.length - 7 > 1 ? 's' : ''}.</p>}
                </div>
              )}
              {ouvert && s.cle === 'assigner' && (
                // Les missions sans ressource (du client au centre, sinon toutes) ;
                // en choisir une ouvre sa fiche d'édition. Dessous, qui est libre.
                <div className="crr-panneau" data-cote={s.x < CENTRE.x ? 'gauche' : 'droite'} role="dialog" aria-label="Missions à pourvoir">
                  <div className="crr-panneau-titre">Missions sans ressource{client ? ` · ${nom}` : ''}</div>
                  {vue.sansRessource.length ? vue.sansRessource.slice(0, 5).map(m => (
                    <button key={m.id} type="button" className="crr-panneau-ligne" onClick={() => { setPanneau(null); onAssigner(m); }}>
                      <span className="crr-panneau-texte"><strong>{m.titre || 'Mission'}</strong><small>{vue.nomClientDe(m)}{m.date_debut ? ` · dès le ${dateCourte(m.date_debut)}` : ''}</small></span>
                    </button>
                  )) : (
                    <p className="crr-panneau-vide">Toutes les missions{client ? ` de ${nom}` : ''} ont leur ressource.</p>
                  )}
                  {vue.disponibles.length > 0 && (
                    <>
                      <div className="crr-panneau-titre crr-panneau-titre--suite">Ressources disponibles</div>
                      {vue.disponibles.slice(0, 4).map(({ r, des, part }) => (
                        <button key={r.id} type="button" className="crr-panneau-ligne crr-panneau-ligne--rang" onClick={() => { setPanneau(null); onOuvrirRessource(r); }}>
                          <span className="crr-panneau-avatar">{initiales(nomRessource(r))}</span>
                          <span className="crr-panneau-texte"><strong>{nomRessource(r)}</strong><small>{[r.mission_titre, r.client].filter(Boolean).join(' · ') || 'Ressource'}</small></span>
                          <span className="crr-panneau-quand">{part < 100 ? `Libre à ${part} %` : des ? `Dès le ${dateCourte(des)}` : 'Libre'}</span>
                        </button>
                      ))}
                    </>
                  )}
                  <button type="button" className="crr-panneau-action" onClick={() => { setPanneau(null); onCreerMission(client); }}>
                    <BI icon="plus-lg" /> Créer une mission{client ? ` pour ${nom}` : ''}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
